import { capitalize } from "@mui/material";
import type { FC } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  innerItemEditStyles,
  Log,
  TIME_END_DAY_HHmm,
  ZERO_TIME_HHmm,
} from "@ryde/shared";

import {
  ColorIconButton,
  NumberInputByControl,
  SelectBoundInputByControl,
  TimeInputByControl,
} from "../../../controls";
import { RatioOvernightMarker } from "./ratio-overnight-marker";
import type {
  IRatioFormItem,
  IRatioItem,
  IRatioItemFormProps,
} from "./table-ratios.models";
import { RatioItemEditStyled } from "./table-ratios.styled";
import { getRatioRowErrorKey } from "./table-ratios.utils";

export const RatioItemEdit: FC<IRatioItemFormProps> = ({
  index,
  value,
  onCancel,
  setValue: setValueParentForm,
  getValues: getValuesParentForm,
  fieldName,
}) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm<Partial<IRatioFormItem>>({
    defaultValues: {
      ...value,
      // The time pickers render a placeholder for an empty value while the form value stays unset,
      // so an unset bound looks filled yet fails its required rule and disables save with no visible
      // reason. Opening on a whole-day window keeps what is shown and what is held in agreement.
      from: value?.from || ZERO_TIME_HHmm,
      to: value?.to || TIME_END_DAY_HHmm,
    },
    mode: "all",
  });

  const windowWatched = useWatch({ control, name: ["from", "to"] });

  const updateValuesHandler = handleSubmit(data => {
    const targetField = `${fieldName || ""}.${index || 0}`;

    const dataNormalized: IRatioItem = {
      ratio: Number(data.ratio),
      areaId: data?.areaId ? +data.areaId : undefined,
      from: data.from || ZERO_TIME_HHmm,
      to: data.to || TIME_END_DAY_HHmm,
    };

    const rowErrorKey = getRatioRowErrorKey(dataNormalized);

    if (rowErrorKey) {
      // `fieldName` is only read by the missing-area message; the other keys ignore it.
      Log.warnAndToast(
        t(rowErrorKey, { fieldName: capitalize(t("area.single")) }),
        dataNormalized,
      );
      return;
    }

    const existingRatios: IRatioItem[] = getValuesParentForm?.(fieldName);
    // `index` addresses the stored array, so the row being edited is the one dropped here.
    const otherRatios =
      typeof index === "number" && existingRatios?.length
        ? existingRatios.toSpliced(index, 1)
        : existingRatios || [];

    const combinationExists = otherRatios.some(
      x => x.areaId === dataNormalized.areaId && x.from === dataNormalized.from,
    );

    if (combinationExists) {
      Log.warnAndToast(t("general.ITEM_ALREADY_EXISTS_MESSAGE"));
      return;
    }

    Log.debug(`Set value for ratio item target field:  ${targetField}`, data);
    setValueParentForm?.(targetField, dataNormalized, {
      shouldValidate: true,
      shouldDirty: true,
    });
    onCancel?.();
  });

  return (
    <RatioItemEditStyled className="no-blur-item" style={innerItemEditStyles}>
      <SelectBoundInputByControl
        name={"areaId"}
        control={control}
        formLabel={capitalize(t("area.single"))}
        formValidation={{
          type: "number",
          rules: [
            {
              validation: "required",
            },
          ],
        }}
        formAttributes={{
          dropItemsSource: "area",
        }}
      />

      <div className="from_to_container">
        <TimeInputByControl
          name={"from"}
          control={control}
          formLabel={t("general.FROM")}
          formValidation={{
            type: "mixed",
            rules: [
              {
                validation: "required",
              },
            ],
          }}
        />
        <TimeInputByControl
          name={"to"}
          control={control}
          formLabel={t("general.TO")}
          formValidation={{
            type: "mixed",
            rules: [
              {
                validation: "required",
              },
            ],
          }}
        />
        <span className="overnight_marker">
          <RatioOvernightMarker
            from={windowWatched?.[0] || ""}
            to={windowWatched?.[1] || ""}
          />
        </span>
      </div>

      <div className="buttons_edit_view">
        <NumberInputByControl
          name={"ratio"}
          control={control}
          formLabel={`${t("general.RATIO")}`}
          formValidation={{
            type: "number",
            rules: [
              {
                validation: "required",
              },
            ],
          }}
        />
        <ColorIconButton
          iconType="CHECK"
          edge="end"
          aria-label="save"
          disabled={!isValid}
          onClick={updateValuesHandler}
        />

        <ColorIconButton
          iconType="CANCEL"
          edge="end"
          aria-label="cancel"
          onClick={() => onCancel?.()}
        />
      </div>
    </RatioItemEditStyled>
  );
};
